import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, Users, X } from 'lucide-react';
import './studentPicker.css';

/* One way to choose students, everywhere a teacher does (Sandy 2026-10-29:
   "really bad drop down and organization for choosing students"; the redesign
   approved the same day, "Pick students" starting empty). House parts only.
   WhoPill: the practice-set box's Who pill; it opens the house drop-down
   (a white panel, a check on the picked row), never a native <select>.
   StudentPicker: "Who gets it" and "N of M students", one search box, ink
   Select all / Clear, chips for who is left out (most are picked) or who is
   picked (half or fewer), then the class in name order with the house 18px
   checkbox: the first 12, then "Show all N students". Places: the practice-set
   box (Home draft card, Create Assignments review, Assign again), the Who step,
   the Also give to rows, Give to another class, and assessments (the saved
   card's Assign it, the assign card). */

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true });
// Search ignores case and accents: "zoe" finds "Zoë".
const fold = s => String(s || '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
const CHIPS = 12;
// Phones show the first 8 students before "Show all", other screens 12.
const PHONE = '(max-width: 600px)';
function usePhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return undefined;
    const update = () => setPhone(mq.matches);
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return phone;
}

// who: 'class' | 'pick'. total: the class size, for "N of M students" in the menu.
export function WhoPill({ who, studentIds = [], total = null, onChange, busy = false }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const button = useRef(null);
  const list = useRef(null);
  const n = studentIds.length;
  const options = [
    { value: 'class', label: 'Whole class', note: 'Everyone in this class' },
    { value: 'pick', label: 'Pick students', note: who === 'pick' ? (total ? `${n} of ${total} students` : `${n} ${n === 1 ? 'student' : 'students'}`) : 'Choose who gets it' },
  ];
  const at = Math.max(0, options.findIndex(o => o.value === who));
  useEffect(() => {
    if (!open) return undefined;
    setActive(at);
    list.current?.focus();
    const away = e => { if (!root.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = o => { setOpen(false); button.current?.focus(); if (o.value !== who) onChange({ who: o.value }); };
  const onButtonKey = e => { if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); } };
  const onListKey = e => {
    const last = options.length - 1;
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); button.current?.focus(); }
    else if (e.key === 'Tab') setOpen(false);
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(last, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(last); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(options[active]); }
  };
  return <span ref={root} className="dc-pill dc-who sp-who">
    <button ref={button} type="button" className="sp-who-btn" aria-haspopup="listbox" aria-expanded={open} aria-label={`Who gets it: ${options[at].label}`}
      disabled={busy} onClick={() => setOpen(o => !o)} onKeyDown={onButtonKey}>
      <Users size={15} aria-hidden="true" /><span>{options[at].label}</span><ChevronDown size={14} className="sp-who-chev" aria-hidden="true" />
    </button>
    {open && <ul ref={list} role="listbox" tabIndex={-1} aria-label="Who gets it" aria-activedescendant={`${id}-o${active}`} className="sp-menu" onKeyDown={onListKey}>
      {options.map((o, i) => <li key={o.value} id={`${id}-o${i}`} role="option" aria-selected={o.value === who} className={i === active ? 'is-active' : undefined}
        onMouseEnter={() => setActive(i)} onMouseDown={e => e.preventDefault()} onClick={() => pick(o)}>
        <span><span className="sp-menu-label">{o.label}</span><small>{o.note}</small></span>
        {o.value === who && <Check size={15} strokeWidth={2.5} aria-hidden="true" />}
      </li>)}
    </ul>}
  </span>;
}

// students: the class roster ([{ userId, name, email? }]; null while it loads).
// studentIds: who is picked; onChange(ids) gets the new list. shown: rows
// before "Show all" (default 12, 8 on phones). error + onRetry:
// the roster could not load. autoFocus: put the cursor in the search box once
// the list is ready (after the teacher chose Pick students).
export default function StudentPicker({ students, studentIds = [], onChange, busy = false, error = false, onRetry = null, title = 'Who gets it', shown: first = null, autoFocus = false }) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);
  const search = useRef(null);
  const phone = usePhone();
  const shown = first ?? (phone ? 8 : 12);
  const sorted = useMemo(() => [...(students || [])].sort(byName), [students]);
  const ready = !error && sorted.length > 0;
  useEffect(() => { if (autoFocus && ready) search.current?.focus({ preventScroll: true }); }, [autoFocus, ready]);

  if (error) return <p className="sp-problem" role="alert">Couldn’t load this class’s students. {onRetry && <button type="button" className="sp-act" onClick={onRetry}>Try again</button>}</p>;
  if (!students) return <p className="sp-note" role="status">Loading your students…</p>;
  if (!sorted.length) return <p className="sp-note">No students are enrolled in this class yet.</p>;

  const on = new Set(studentIds);
  const picked = sorted.filter(s => on.has(s.userId));
  const left = sorted.filter(s => !on.has(s.userId));
  const q = fold(query.trim());
  const matches = q ? sorted.filter(s => fold(s.name).includes(q) || fold(s.email).includes(q)) : sorted;
  const rows = q ? matches : all ? sorted : sorted.slice(0, shown);
  const set = ids => onChange([...new Set(ids)]);
  const toggle = sid => set(on.has(sid) ? studentIds.filter(x => x !== sid) : [...studentIds, sid]);
  // Searching, Select all and Clear act on the students found.
  const scope = q ? matches : sorted;
  const allOn = scope.every(s => on.has(s.userId));
  const noneOn = !scope.some(s => on.has(s.userId));
  const selectAll = () => set([...studentIds, ...scope.map(s => s.userId)]);
  const clear = () => (q ? set(studentIds.filter(x => !scope.some(s => s.userId === x))) : set([]));
  // Most picked: show who is left out. Half or fewer: show who is picked.
  const few = picked.length <= sorted.length / 2;
  const chips = few ? picked : left;
  const showChips = picked.length > 0 && chips.length > 0 && chips.length < sorted.length;

  return <div className="sp" role="group" aria-labelledby={`${id}-t`}>
    <div className="sp-head"><span className="sp-title" id={`${id}-t`}>{title}</span><span className="sp-count" aria-live="polite">{picked.length} of {sorted.length} students</span></div>
    <div className="sp-tools">
      <label className="sp-search"><Search size={16} aria-hidden="true" />
        <input ref={search} type="search" placeholder="Find a student" aria-label="Find a student" value={query} disabled={busy} autoComplete="off" spellCheck={false}
          onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Escape' && query) { e.preventDefault(); e.stopPropagation(); setQuery(''); } }} /></label>
      <span className="sp-acts">
        <button type="button" className="sp-act" disabled={busy || !scope.length || allOn} onClick={selectAll}>{q ? 'Select these' : 'Select all'}</button>
        <button type="button" className="sp-act" disabled={busy || noneOn} onClick={clear}>{q ? 'Clear these' : 'Clear'}</button>
      </span>
    </div>
    {showChips && <div className="sp-chips">
      <span className="sp-chips-label">{few ? 'Picked' : 'Left out'}</span>
      {chips.slice(0, CHIPS).map(s => <button key={s.userId} type="button" className="sp-chip" disabled={busy}
        aria-label={few ? `Take out ${s.name}` : `Put ${s.name} back in`} title={few ? 'Take out' : 'Put back in'} onClick={() => toggle(s.userId)}>
        <span>{s.name}</span><X size={13} aria-hidden="true" /></button>)}
      {chips.length > CHIPS && <span className="sp-chips-more">and {chips.length - CHIPS} more</span>}
    </div>}
    {rows.length > 0 && <ul className="sp-list">{rows.map(s => {
      const is = on.has(s.userId);
      return <li key={s.userId}><label className={`sp-row${is ? '' : ' is-off'}`}>
        <input type="checkbox" className="sp-input" checked={is} disabled={busy} onChange={() => toggle(s.userId)} />
        <span className={`sp-box${is ? ' is-on' : ''}`} aria-hidden="true">{is && <Check size={12} strokeWidth={3.5} />}</span>
        <span className="sp-name">{s.name}</span>
      </label></li>;
    })}</ul>}
    {q && !matches.length && <p className="sp-note" role="status">No students match “{query.trim()}”.</p>}
    {!q && sorted.length > shown && <button type="button" className="sp-more" aria-expanded={all} onClick={() => setAll(a => !a)}>{all ? 'Show fewer' : `Show all ${sorted.length} students`}</button>}
  </div>;
}
